class RelaxedParseError extends Error {}

class RelaxedParser {
  constructor(text) {
    this.text = text;
    this.pos = 0;
  }

  error(msg) {
    throw new RelaxedParseError(`${msg} at position ${this.pos}`);
  }

  peek() {
    return this.text[this.pos];
  }

  skipWhitespace() {
    while (this.pos < this.text.length && /\s/.test(this.text[this.pos])) this.pos++;
  }

  parse() {
    this.skipWhitespace();
    const value = this.parseValue();
    this.skipWhitespace();
    if (this.pos < this.text.length) this.error('Unexpected trailing content');
    return value;
  }

  parseValue() {
    this.skipWhitespace();
    const ch = this.peek();
    if (ch === undefined) this.error('Unexpected end of input');
    if (ch === '{') return this.parseObject();
    if (ch === '[') return this.parseArray();
    if (ch === '"' || ch === "'") return this.parseString();
    if (ch === '-' || /[0-9]/.test(ch)) return this.parseNumber();
    if (/[A-Za-z_$]/.test(ch)) return this.parseBareword();
    this.error(`Unexpected character '${ch}'`);
  }


  parseBareword() {
    const start = this.pos;
    while (this.pos < this.text.length && /[A-Za-z0-9_$]/.test(this.text[this.pos])) this.pos++;
    const word = this.text.slice(start, this.pos);
    if (word === 'true') return true;
    if (word === 'false') return false;
    if (word === 'null' || word === 'undefined') return null;
    return word;
  }

  parseString() {
    const quote = this.peek();
    this.pos++;
    let result = '';
    while (this.pos < this.text.length && this.text[this.pos] !== quote) {
      const c = this.text[this.pos];
      if (c === '\\') {
        this.pos++;
        const esc = this.text[this.pos];
        const map = { n: '\n', t: '\t', r: '\r', '"': '"', "'": "'", '\\': '\\' };
        result += map[esc] !== undefined ? map[esc] : (esc || '');
      } else {
        result += c;
      }
      this.pos++;
    }
    if (this.text[this.pos] !== quote) this.error('Unterminated string');
    this.pos++;
    return result;
  }

  parseNumber() {
    const start = this.pos;
    if (this.peek() === '-') this.pos++;
    while (this.pos < this.text.length && /[0-9.eE+-]/.test(this.text[this.pos])) this.pos++;
    const num = Number(this.text.slice(start, this.pos));
    if (Number.isNaN(num)) this.error('Invalid number');
    return num;
  }

  parseKey() {
    this.skipWhitespace();
    const ch = this.peek();
    if (ch === '"' || ch === "'") return this.parseString();
    if (/[A-Za-z_$]/.test(ch)) {
      const start = this.pos;
      while (this.pos < this.text.length && /[A-Za-z0-9_$]/.test(this.text[this.pos])) this.pos++;
      return this.text.slice(start, this.pos);
    }
    if (/[0-9]/.test(ch)) {
      const start = this.pos;
      while (this.pos < this.text.length && /[0-9]/.test(this.text[this.pos])) this.pos++;
      return this.text.slice(start, this.pos);
    }
    this.error(`Expected object key, got '${ch}'`);
  }

  parseObject() {
    const objStart = this.pos;

    // "{ N keys }" — a summary string a formatter substituted for the real (truncated)
    // contents. Checked BEFORE normal key:value parsing since it isn't real object syntax.
    const placeholderMatch = this.text.slice(objStart).match(/^\{\s*\d+\s+keys?\s*\}/);
    if (placeholderMatch) {
      this.pos = objStart + placeholderMatch[0].length;
      return { __placeholder: true, label: placeholderMatch[0].replace(/\s+/g, ' ').trim() };
    }

    this.pos++; // consume '{'
    this.skipWhitespace();
    const obj = {};
    if (this.peek() === '}') {
      this.pos++;
      return obj;
    }
    while (true) {
      this.skipWhitespace();
      const key = this.parseKey();
      this.skipWhitespace();
      if (this.peek() !== ':') this.error("Expected ':' after key");
      this.pos++;
      obj[key] = this.parseValue();
      this.skipWhitespace();
      if (this.peek() === ',') {
        this.pos++;
        this.skipWhitespace();
        if (this.peek() === '}') { this.pos++; break; } // trailing comma
        continue;
      }
      if (this.peek() === '}') { this.pos++; break; }
      this.error("Expected ',' or '}'");
    }
    return obj;
  }

  parseArray() {
    const arrStart = this.pos;

    // "[Array]" / "[Object]" / "[Circular *1]" — same idea as the object placeholder above.
    const placeholderMatch =
      this.text.slice(arrStart).match(/^\[(Array|Object)(?:\s*\(\s*\d+\s*\))?\]/) ||
      this.text.slice(arrStart).match(/^\[Circular[^\]]*\]/);
    if (placeholderMatch) {
      this.pos = arrStart + placeholderMatch[0].length;
      return { __placeholder: true, label: placeholderMatch[0] };
    }

    this.pos++; // consume '['
    this.skipWhitespace();
    const arr = [];
    if (this.peek() === ']') {
      this.pos++;
      return arr;
    }
    while (true) {
      arr.push(this.parseValue());
      this.skipWhitespace();
      if (this.peek() === ',') {
        this.pos++;
        this.skipWhitespace();
        if (this.peek() === ']') { this.pos++; break; }
        continue;
      }
      if (this.peek() === ']') { this.pos++; break; }
      this.error("Expected ',' or ']'");
    }
    return arr;
  }
}

// Returns the parsed value, or null if this text isn't parseable even under the relaxed
// grammar (caller should treat that the same as a strict-JSON parse failure).
export function parseRelaxed(text) {
  try {
    return new RelaxedParser(text).parse();
  } catch (e) {
    if (e instanceof RelaxedParseError) return null;
    throw e;
  }
}

export function isPlaceholder(value) {
    console.log({value})
  return value !== null && typeof value === 'object' && value.__placeholder === true;
}